import { apiRequest } from '@/shared/api/client';
import type { Envelope } from '@/shared/types/api';

import type { QuickFindRequest, QuickFindResult, SearchPortal } from './types';

/**
 * Quick Find — one read per portal, each on that portal's own guarded route
 * (CONTRACT §2). Read-only by construction: a GET, no idempotency key, no
 * body. The caller's `AbortSignal` is forwarded, so a superseded search is
 * cancelled on the network, not merely ignored when it lands.
 *
 * Nothing about WHO is searching is sent: no tenant, supplier or branch id.
 * The server takes all of that from the session.
 */
const PATHS: Record<SearchPortal, string> = {
  tenant: '/search',
  vendor: '/vendor/search',
  platform: '/platform/search',
};

function quickFind(portal: SearchPortal, request: QuickFindRequest): Promise<QuickFindResult> {
  return apiRequest<Envelope<QuickFindResult>>(PATHS[portal], {
    query: {
      q: request.q,
      'types[]': request.types,
      limit: request.limit,
    },
    signal: request.signal,
  }).then((response) => response.data);
}

/** The buying organisation's workspace: catalogue, requests, orders, RFQs, suppliers. */
export function searchTenant(request: QuickFindRequest): Promise<QuickFindResult> {
  return quickFind('tenant', request);
}

/** The supplier portal: this supplier's own orders, RFQ invitations and (where the buyer is entitled) invoices. */
export function searchVendor(request: QuickFindRequest): Promise<QuickFindResult> {
  return quickFind('vendor', request);
}

/** The operator console: organisations only — never a customer's procurement. */
export function searchPlatform(request: QuickFindRequest): Promise<QuickFindResult> {
  return quickFind('platform', request);
}

export const QUICK_FIND = {
  tenant: searchTenant,
  vendor: searchVendor,
  platform: searchPlatform,
} as const satisfies Record<SearchPortal, (request: QuickFindRequest) => Promise<QuickFindResult>>;
